/**
 * AI Clips data helpers.
 * Queries run through the app Apollo client (fs-backoffice App.tsx); JWT is sent via authLink.
 * The clip field on Interview is resolved via DataLoader on the backend.
 */
import { ApolloClient } from '@apollo/client';
import {
  SortDirection,
  InterviewerTasksOrderBy,
  InterviewSourceType,
} from 'api/graphql-global-types';
import {
  GET_INTERVIEWS_WITH_CLIPS,
  GET_AI_CLIP,
  GET_AI_CLIP_PRESIGNED_URL,
  GET_STREAM_RECORDINGS,
  ADD_AI_CLIP_NOTE,
  UPDATE_AI_CLIP_NOTES,
  LOOKUP_STORE_USER,
} from './queries';
import {
  AIClipListItem,
  AIClipRecord,
  StreamRecording,
  UploadedClipFile,
} from './types';

type AppApolloClient = ApolloClient<any>;

export interface FetchClipRecordsPageOptions {
  limit?: number;
  offset?: number;
  direction?: 'ASC' | 'DESC';
  orderBy?: InterviewerTasksOrderBy;
  /** Server-side filter: partial interview id */
  id?: string;
  /** Server-side filter: search by slug (interviewee, interviewer, or stream) */
  slugSearch?: string;
  /** Server-side filter: host name, guest name, or interview title, ORed by the API */
  nameSearch?: string;
}

export interface FetchClipRecordsPageResult {
  entities: AIClipListItem[];
  total: number;
  offset: number;
  limit: number;
}

interface InterviewsWithClipsQueryResult {
  getInterviews: {
    entities: InterviewsWithClipsEntity[];
    total: number;
    offset: number;
    limit: number;
  };
}

interface InterviewParticipant {
  id: string;
  slug: string | null;
  firstName?: string | null;
  lastName?: string | null;
  storeDetails?: { storeName: string | null } | null;
}

interface InterviewsWithClipsEntity {
  id: string;
  createdAt: string;
  status: string;
  finalStreamTitle?: string | null;
  interviewer?: InterviewParticipant | null;
  interviewee?: InterviewParticipant | null;
  stream?: {
    slug: string;
    store?: InterviewParticipant | null;
  } | null;
  clip: AIClipRecord | null;
}

interface InterviewsQueryInput {
  limit: number;
  offset: number;
  direction: SortDirection;
  orderBy: InterviewerTasksOrderBy;
  statuses: string[];
  sources?: InterviewSourceType[];
  id?: string;
  slugSearch?: string;
  nameSearch?: string;
}

const AI_CLIP_LIST_EXCLUDED_SOURCES: InterviewSourceType[] = [
  InterviewSourceType.InterviewNow,
];

const AI_CLIP_LIST_SOURCES: InterviewSourceType[] = Object.values(
  InterviewSourceType
).filter((source) => !AI_CLIP_LIST_EXCLUDED_SOURCES.includes(source));

function getParticipantDisplayName(
  participant: InterviewParticipant | null | undefined
): string | undefined {
  if (!participant) return undefined;
  if (participant.storeDetails?.storeName) {
    return participant.storeDetails.storeName;
  }
  const first = participant.firstName?.trim();
  const last = participant.lastName?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return undefined;
}

function getUniqueCandidateUserSlugs(
  interview: InterviewsWithClipsEntity
): string[] {
  return [interview.interviewer?.slug, interview.interviewee?.slug].filter(
    (slug, index, arr): slug is string => !!slug && arr.indexOf(slug) === index
  );
}

function getUserIdFromInterview(
  interview: InterviewsWithClipsEntity,
  userSlug: string
): string {
  const { interviewer, interviewee } = interview;
  if (userSlug && userSlug === interviewer?.slug) return interviewer?.id ?? '';
  if (userSlug && userSlug === interviewee?.slug) return interviewee?.id ?? '';
  return interviewer?.id ?? interviewee?.id ?? '';
}

function resolveClipProfileName(
  interview: InterviewsWithClipsEntity,
  clip: AIClipRecord | null
): string | undefined {
  const { interviewer, interviewee } = interview;
  const clipUserSlug = clip?.userId;

  if (clipUserSlug && clipUserSlug === interviewer?.slug) {
    return (
      getParticipantDisplayName(interviewer) ||
      getParticipantDisplayName(interviewee) ||
      clip?.profileName ||
      undefined
    );
  }

  if (clipUserSlug && clipUserSlug === interviewee?.slug) {
    return (
      getParticipantDisplayName(interviewee) ||
      getParticipantDisplayName(interviewer) ||
      clip?.profileName ||
      undefined
    );
  }

  return (
    getParticipantDisplayName(interviewee) ||
    getParticipantDisplayName(interviewer) ||
    clip?.profileName ||
    undefined
  );
}

async function fetchInterviewsPage(
  client: AppApolloClient,
  input: InterviewsQueryInput
): Promise<InterviewsWithClipsQueryResult['getInterviews']> {
  const result = await client.query<InterviewsWithClipsQueryResult>({
    query: GET_INTERVIEWS_WITH_CLIPS,
    variables: { input },
    fetchPolicy: 'network-only',
  });

  return (
    result.data?.getInterviews ?? {
      entities: [],
      total: 0,
      offset: input.offset,
      limit: input.limit,
    }
  );
}

function mapInterviewsToClipListItems(
  interviews: InterviewsWithClipsEntity[]
): AIClipListItem[] {
  return interviews.map((interview) => {
    const clip = interview.clip;
    const candidateUserSlugs = getUniqueCandidateUserSlugs(interview);
    const userSlug = clip?.userId || candidateUserSlugs[0] || '';
    const streamSlug = clip?.interviewId || interview.stream?.slug || '';
    const userId = getUserIdFromInterview(interview, userSlug);

    return {
      interviewId: interview.id,
      userId,
      userSlug,
      streamSlug,
      interviewName: interview.finalStreamTitle || undefined,
      interviewCreatedAt: String(interview.createdAt || ''),
      interviewStatus: String(interview.status || ''),
      profileName: resolveClipProfileName(interview, clip),
      hostName:
        getParticipantDisplayName(interview.interviewer) ||
        getParticipantDisplayName(interview.stream?.store) ||
        undefined,
      guestName: interview.interviewee
        ? getParticipantDisplayName(interview.interviewee)
        : undefined,
      clip,
    };
  });
}

export async function fetchClipRecordsPage(
  client: AppApolloClient,
  options?: FetchClipRecordsPageOptions
): Promise<FetchClipRecordsPageResult> {
  const limit = options?.limit ?? 20;
  const offset = options?.offset ?? 0;
  const direction = options?.direction ?? 'DESC';
  const id = options?.id?.trim();
  const slugSearch = options?.slugSearch?.trim();
  const nameSearch = options?.nameSearch?.trim();

  const result = await fetchInterviewsPage(client, {
    limit,
    offset,
    direction: direction === 'ASC' ? SortDirection.ASC : SortDirection.DESC,
    orderBy: options?.orderBy ?? InterviewerTasksOrderBy.createdAt,
    statuses: ['Completed'],
    sources: AI_CLIP_LIST_SOURCES,
    ...(id ? { id } : {}),
    ...(slugSearch ? { slugSearch } : {}),
    ...(nameSearch ? { nameSearch } : {}),
  });

  return {
    entities: mapInterviewsToClipListItems(result.entities ?? []),
    total: result.total ?? 0,
    offset,
    limit,
  };
}

export async function fetchClipRecord(
  client: AppApolloClient,
  userSlug: string,
  streamSlug: string
): Promise<AIClipRecord | null> {
  const result = await client.query<{ getAiClip: AIClipRecord | null }>({
    query: GET_AI_CLIP,
    variables: { userSlug, streamSlug },
    fetchPolicy: 'network-only',
  });
  return result.data?.getAiClip ?? null;
}

/** The stream's recordings in air-time order, as the backend returns them. */
export async function fetchStreamRecordings(
  client: AppApolloClient,
  streamSlug: string
): Promise<StreamRecording[]> {
  const result = await client.query<{
    adminStreams: {
      entities: Array<{ streamRecords: StreamRecording[] | null }>;
    };
  }>({
    query: GET_STREAM_RECORDINGS,
    variables: { slug: streamSlug },
    fetchPolicy: 'network-only',
  });

  return result.data?.adminStreams?.entities?.[0]?.streamRecords ?? [];
}

export async function getPresignedUrl(
  client: AppApolloClient,
  s3Uri: string
): Promise<string> {
  const result = await client.query<{ getAiClipPresignedUrl: string }>({
    query: GET_AI_CLIP_PRESIGNED_URL,
    variables: { s3Uri },
    fetchPolicy: 'network-only',
  });
  return result.data?.getAiClipPresignedUrl ?? '';
}

export async function updateClipNotes(
  client: AppApolloClient,
  userSlug: string,
  streamSlug: string,
  notes: Record<string, string>
): Promise<AIClipRecord['notes']> {
  const result = await client.mutate<{
    updateAiClipNotes: Pick<AIClipRecord, 'notes'> | null;
  }>({
    mutation: UPDATE_AI_CLIP_NOTES,
    variables: {
      input: {
        userSlug,
        streamSlug,
        notes: Object.entries(notes).map(([clipName, note]) => ({
          clipName,
          note,
        })),
      },
    },
  });

  return result.data?.updateAiClipNotes?.notes ?? [];
}

export async function addClipNote(
  client: AppApolloClient,
  userSlug: string,
  streamSlug: string,
  clipName: string,
  note: string
): Promise<AIClipRecord['notes']> {
  const result = await client.mutate<{
    addAiClipNote: Pick<AIClipRecord, 'notes'> | null;
  }>({
    mutation: ADD_AI_CLIP_NOTE,
    variables: {
      input: {
        userSlug,
        streamSlug,
        clipName,
        note,
      },
    },
  });

  return result.data?.addAiClipNote?.notes ?? [];
}

/**
 * Look up user IDs via adminStores by slug or store name.
 * Returns matching user ids (empty array if none found).
 */
export async function lookupStoreUserIds(
  client: AppApolloClient,
  filter: { slug?: string; storeName?: string }
): Promise<string[]> {
  const result = await client.query<{
    adminStores: { entities: Array<{ id: string }> };
  }>({
    query: LOOKUP_STORE_USER,
    variables: {
      input: { ...filter, limit: 20, offset: 0 },
    },
    fetchPolicy: 'network-only',
  });
  return result.data?.adminStores?.entities?.map((e) => e.id) ?? [];
}

export function sortedClipFiles(files: UploadedClipFile[]): UploadedClipFile[] {
  return [...files].sort((a, b) => a.name.localeCompare(b.name));
}

export function isVideoFile(filename: string): boolean {
  return /\.(mp4|mov|webm)$/i.test(filename);
}

export function isThumbnailFile(filename: string): boolean {
  return /\.(jpg|jpeg|png|webp)$/i.test(filename);
}
